"use client";

import { useEffect, useState, type ReactNode } from "react";
import Image from "@/components/ui/image";
import { getCountryName } from "@/lib/countries";
import { cn } from "@/lib/utils";
import type { NewsletterShowcase, ShowcaseDesign } from "@/lib/newsletter/showcase";

const compactUsd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
/** Sensor Tower floors small apps at "< $5K"; below this an estimate says nothing worth showing. */
const MIN_REVENUE_SHOWN = 10_000;
const ROTATE_MS = 5000;

/**
 * The right half of the App Store Times modal: a sample of the two kinds of email,
 * from our own data. A chart climb (drawn once as the modal opens) and a carousel
 * of the most-viewed apps on the site, each as three captured screens.
 */
export function NewsletterShowcasePanel({ showcase, loading, className }: { showcase: NewsletterShowcase | undefined; loading: boolean; className?: string }) {
	const climber = showcase?.climber ?? null;
	const designs = showcase?.designs ?? [];

	if (loading && !showcase) {
		return (
			<div className={cn("flex flex-col gap-3", className)} aria-hidden="true">
				<div className="h-8" />
				<div className="h-[188px] animate-pulse rounded-2xl bg-white/[0.04]" />
				<div className="h-[372px] animate-pulse rounded-2xl bg-white/[0.03]" />
			</div>
		);
	}
	if (!climber && designs.length === 0) return null;

	return (
		<div className={cn("flex flex-col gap-3", className)}>
			<p className="flex h-8 items-center pr-8 text-xs font-medium text-[rgb(var(--ast-brand-text-rgb)/0.6)]">Inside App Store Times</p>
			{climber && <ClimbCard climber={climber} />}
			{designs.length > 0 && <DesignCard designs={designs} />}
		</div>
	);
}

function Card({ title, stat, children, footer }: { title: string; stat: ReactNode; children: ReactNode; footer: ReactNode }) {
	return (
		<figure className="rounded-2xl border border-[rgb(var(--ast-brand-moss-rgb)/0.55)] bg-white/[0.025] p-4">
			<div className="flex items-baseline justify-between gap-3">
				<span className="text-xs font-semibold text-[var(--ast-brand-green)]">{title}</span>
				<span className="text-base font-bold tabular-nums text-[var(--ast-brand-green)]">{stat}</span>
			</div>
			{children}
			<figcaption className="mt-3 flex min-w-0 items-center gap-2.5 text-sm leading-snug text-[rgb(var(--ast-brand-text-rgb)/0.72)]">{footer}</figcaption>
		</figure>
	);
}

function AppIcon({ src }: { src: string | null }) {
	return src ? <Image src={src} alt="" width={24} height={24} className="h-6 w-6 shrink-0 rounded-[6px] ring-1 ring-white/10" /> : null;
}

function ClimbCard({ climber }: { climber: NonNullable<NewsletterShowcase["climber"]> }) {
	const place = climber.country === "us" ? "the US" : (getCountryName(climber.country) ?? climber.country.toUpperCase());
	// Scaled to this climb, rank 1 up. The top 34% is left for the end rank's label.
	const lo = Math.max(1, climber.to - 8);
	const hi = climber.from + 8;
	const y = (rank: number) => 34 + ((rank - lo) / Math.max(1, hi - lo)) * 52;
	const y0 = y(climber.from);
	const y1 = y(climber.to);

	return (
		<Card
			title="Chart climbers"
			stat={`+${climber.from - climber.to}`}
			footer={
				<>
					<AppIcon src={climber.iconUrl} />
					<span className="min-w-0 truncate">
						<span className="font-semibold text-[var(--ast-brand-text)]">{climber.name}</span> in {place} charts
					</span>
				</>
			}
		>
			<div className="relative mt-1 h-32">
				<svg viewBox="0 0 200 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
					{[40, 62, 84].map((g) => (
						<line key={g} x1="0" x2="200" y1={g} y2={g} stroke="rgb(var(--ast-brand-moss-rgb))" strokeOpacity="0.5" strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
					))}
					{/* No non-scaling stroke here: Chrome then measures the dash in screen pixels and the draw stops short. */}
					<path d={`M 30 ${y0} C 95 ${y0}, 110 ${y1}, 170 ${y1}`} fill="none" stroke="var(--ast-brand-green)" strokeWidth="2.5" strokeLinecap="round" pathLength={1} className="newsletter-climb-line" />
				</svg>
				{/* Dots and labels are HTML: the stretched SVG would squash circles. */}
				<span className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[rgb(var(--ast-brand-text-rgb)/0.45)] bg-[var(--ast-brand-night)]" style={{ left: "15%", top: `${y0}%` }} />
				<span className="absolute -translate-x-1/2 pt-3 text-xs tabular-nums text-[rgb(var(--ast-brand-text-rgb)/0.6)]" style={{ left: "15%", top: `${y0}%` }}>
					#{climber.from}
				</span>
				<span className="newsletter-climb-end absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--ast-brand-green)] shadow-[0_0_0_6px_rgb(var(--ast-brand-green-rgb)/0.18)]" style={{ left: "85%", top: `${y1}%` }} />
				<span className="newsletter-climb-end absolute -translate-x-1/2 -translate-y-full pb-2 text-3xl font-bold leading-none tabular-nums tracking-tight text-[var(--ast-brand-green)]" style={{ left: "85%", top: `${y1}%` }}>
					#{climber.to}
				</span>
			</div>
		</Card>
	);
}

/**
 * Rotates through the apps on its own, pausing while pointed at or focused. Choosing
 * an app with a dot stops it for good (the way to pause on touch), and only then is
 * the caption announced: an auto-rotating live region would talk over the form.
 */
export function DesignCard({ designs }: { designs: ShowcaseDesign[] }) {
	const [index, setIndex] = useState(0);
	const [paused, setPaused] = useState(false);
	const [chosen, setChosen] = useState(false);
	const active = index % designs.length;
	const design = designs[active];

	useEffect(() => {
		if (paused || chosen || designs.length < 2) return;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		const t = window.setTimeout(() => setIndex((i) => (i + 1) % designs.length), ROTATE_MS);
		return () => window.clearTimeout(t);
	}, [index, paused, chosen, designs.length]);

	const tilts = ["-rotate-[6deg] translate-y-2", "z-10", "rotate-[6deg] translate-y-2"];
	const revenue = design.monthlyRevenue && design.monthlyRevenue >= MIN_REVENUE_SHOWN ? `${compactUsd.format(design.monthlyRevenue)}/mo` : "";

	return (
		<section
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
			onFocusCapture={() => setPaused(true)}
			onBlurCapture={() => setPaused(false)}
			aria-roledescription="carousel"
			aria-label="Most-viewed apps on App Store Tracker"
		>
			<Card
				title="Design breakdowns"
				stat={revenue && <span title="Estimated monthly revenue">{revenue}</span>}
				footer={
					<>
						<AppIcon src={design.iconUrl} />
						<span className="line-clamp-2 min-h-[2.5rem] min-w-0" aria-live={chosen ? "polite" : "off"}>
							<span className="font-semibold text-[var(--ast-brand-text)]">{design.name}</span>, one of the most-viewed apps on App Store Tracker
						</span>
					</>
				}
			>
				<div
					key={design.appId}
					role="group"
					aria-roledescription="slide"
					aria-label={`${active + 1} of ${designs.length}: ${design.name}`}
					className="newsletter-design-swap mt-3 flex justify-center -space-x-5"
				>
					{design.screens.map((screen, i) => (
						<div key={screen.url} className={cn("flex w-[86px] shrink-0 flex-col items-center sm:w-[100px]", tilts[i])}>
							<div className="w-full overflow-hidden rounded-[16px] border-2 border-white/10 bg-black shadow-[0_18px_40px_-12px_rgba(0,0,0,0.85)] ring-1 ring-black">
								{/* Next's optimizer resizes the ~3 MB capture once and caches it, as /designs does for the same files. */}
								<Image
									src={screen.url}
									alt={`${design.name}, ${screen.label.toLowerCase()} screen`}
									width={100}
									height={217}
									sizes="(min-width: 640px) 100px, 86px"
									className="aspect-[1179/2556] h-auto w-full object-cover object-top"
								/>
							</div>
							<span className="mt-2 text-[11px] text-[rgb(var(--ast-brand-text-rgb)/0.65)]">{screen.label}</span>
						</div>
					))}
				</div>
				{designs.length > 1 && (
					<div className="mt-1 flex justify-center">
						{designs.map((d, i) => (
							<button
								key={d.appId}
								type="button"
								onClick={() => {
									setChosen(true);
									setIndex(i);
								}}
								aria-label={`Show ${d.name}`}
								aria-current={i === active}
								className="group flex h-6 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ast-brand-green-rgb)/0.6)]"
							>
								<span
									className={cn(
										"h-1.5 rounded-full transition-all",
										i === active ? "w-4 bg-[var(--ast-brand-green)]" : "w-1.5 bg-[rgb(var(--ast-brand-text-rgb)/0.45)] group-hover:bg-[rgb(var(--ast-brand-text-rgb)/0.7)]",
									)}
								/>
							</button>
						))}
					</div>
				)}
			</Card>
		</section>
	);
}
